import { Injectable, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError } from 'rxjs/operators';

import { ConfigService } from './config.service';

export type SearchHitKind =
  | 'page'
  | 'action'
  | 'customer'
  | 'product'
  | 'order'
  | 'kimbyala'
  | 'ticket'
  | 'recent';

export interface SearchHit {
  kind: SearchHitKind;
  id: string | number;
  title: string;
  subtitle?: string;
  route: string;
  icon: string;
  meta?: Record<string, unknown>;
}

export interface SearchGroup {
  kind: string;
  label: string;
  items: SearchHit[];
}

export interface GlobalSearchResponse {
  q: string;
  groups: SearchGroup[];
  count: number;
}

const RECENT_KEY = 'vd-global-search-recent';
const MAX_RECENT = 8;

@Injectable({ providedIn: 'root' })
export class GlobalSearchService {
  private readonly http = inject(HttpClient);
  private readonly cfg = inject(ConfigService);

  readonly open = signal(false);

  show(): void {
    this.open.set(true);
  }

  hide(): void {
    this.open.set(false);
  }

  toggle(): void {
    this.open.update((v) => !v);
  }

  private base(): string {
    const prod =
      typeof window !== 'undefined' &&
      /^(vendeur\.site|vendor\.codincloud\.com)$/i.test(window.location.hostname);
    return (prod ? '' : this.cfg.apiUrl.replace(/\/+$/, '')) + '/me/search';
  }

  search(q: string, limit = 6): Observable<GlobalSearchResponse> {
    const query = (q || '').trim();
    if (query.length < 1) {
      return of({ q: '', groups: [], count: 0 });
    }
    return this.http
      .get<GlobalSearchResponse>(this.base(), {
        params: { q: query, limit: String(limit) },
      })
      .pipe(catchError(() => of({ q: query, groups: [], count: 0 })));
  }

  getRecent(): SearchHit[] {
    try {
      const raw = localStorage.getItem(RECENT_KEY);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed.slice(0, MAX_RECENT) : [];
    } catch {
      return [];
    }
  }

  pushRecent(hit: SearchHit): void {
    try {
      const prev = this.getRecent().filter(
        (h) => !(h.kind === hit.kind && String(h.id) === String(hit.id)),
      );
      const next = [
        {
          kind: hit.kind,
          id: hit.id,
          title: hit.title,
          subtitle: hit.subtitle,
          route: hit.route,
          icon: hit.icon,
        },
        ...prev,
      ].slice(0, MAX_RECENT);
      localStorage.setItem(RECENT_KEY, JSON.stringify(next));
    } catch {
      /* private mode */
    }
  }
}
